import axios from 'axios';
import _ from 'lodash';
/*
  Custom PlayPosit package for various helper functions/prototypes.
*/
import {
  getServerUrl,
  required,
} from '@utilities';

const service = {
  baseURL: null,
  hasAttemptedTokenRefresh: false,
  async init({
    token = required('token'),
  } = {}) {
    /*
      Use helper functions to determine the correct server URL based on the current environment.
      For you, this should end up being apimatt.know.dog (if Ben has set that up already).
      The express route file is: express/app/routes/v2/profile.js
      The express service file is: express/app/controllers/v2/profileService.js
    */
    this.baseURL = getServerUrl();
    /*
      Make it so that all API calls through Axios will, by default, have their Authorization header set to our PlayPosit token.
    */
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    axios.defaults.headers.common.Accept = 'application/json';
    axios.defaults.headers.common['Content-Type'] = 'application/json';

    /*
      In the event of a 401 error, we first try to refresh the PlayPosit token.
    */
    axios.interceptors.response.use(null, (error) => {
      if (
        _.get(error, 'config.headers.Authorization')
        && [401].includes(_.get(error, 'response.status'))
      ) {
        if (service.hasAttemptedTokenRefresh) return error;
        service.hasAttemptedTokenRefresh = true;
        const oldToken = error.config.headers.Authorization.replace('Bearer ', '');
        return service.refreshToken(oldToken)
          .then(axios.request(error.config))
          .catch(Promise.reject);
      }
      return Promise.reject(error.response.data);
    });
  },
  refreshToken(oldToken) {
    return axios.post(`${service.baseURL}authnew/refreshtoken`, {
      old_token: oldToken,
    });
  },
  attachment: {
    create(file) {
      return axios.post(`${service.baseURL}attachment`, file);
    },
    rename(playlistId, resource) {
      return axios.put(`${service.baseURL}playlist/${playlistId}/resources/name`, { resource });
    },
    reorder(playlistId, attachments) {
      return axios.put(`${service.baseURL}playlist/${playlistId}/resources`, { attachments });
    },
    update(data) {
      return axios.put(`${service.baseURL}attachment`, data);
    },
    upload(data) {
      return axios.put(`${data.url}`, data.file, {
        headers: { 'Content-Type': 'application/octet-stream' },
        transformRequest: [(d, headers) => { delete headers.Authorization; return d; }],
      });
    },
  },
  badgr: {
    assert({
      playlistId,
      userData,
    } = {}) {
      return axios.post(`${service.baseURL}badgr/assert/${playlistId}`, userData);
    },
    createBadgeClass(playlistId, badge, issuerEntityId) {
      return axios.post(`${service.baseURL}badgr/badge-class/${playlistId}`,
        {
          badge,
          issuerEntityId,
        });
    },
    getAssertion({
      badgeEntityId,
      schoolId,
    } = {}) {
      return axios.get(`${service.baseURL}badgr/assertion/${badgeEntityId}?schoolId=${schoolId}`);
    },
    getBadges(entityId) {
      return axios.get(`${service.baseURL}badgr/badges/${entityId}`);
    },
    issuers() {
      return axios.get(`${service.baseURL}badgr`);
    },
    requestIssuer(issuerProfile) {
      return axios.post(`${service.baseURL}badgr/issuer-request`, { issuerProfile });
    },
  },
  credly: {
    assertion(data) {
      return axios.get(`${service.baseURL}credly/playlist/${data.playlistId}/assertion?schoolId=${data.schoolId}&badgeTemplateId=${data.badgeTemplateId}`);
    },
    issue(data) {
      return axios.post(`${service.baseURL}credly/issue`, data);
    },
    getBadges(schoolId) {
      return axios.get(`${service.baseURL}credly/badges?schoolId=${schoolId}`);
    },
  },
  dash: {
    assign(token, playlistId) {
      window.open(`${service.baseURL}dash/instructor/Playlists/Playlists?page=1&per_page=20&search=&sort=&section_id=&bulb_id=&interaction_id=&interaction_type=&student_id=&folder_id=&subfolder_id=&lms_class=false&lms_launch=false&lmb_bulb=&lmb_section=&playlistGradebook&playlistAssign=${playlistId}&token=${token}`, '_blank');
    },
    monitor(sectionId, token, playlistId) {
      window.open(`${service.baseURL}dash/instructor/Classes/Gradebook/?page=1&per_page=20&search=&sort=&section_id=${sectionId}&token=${token}&bulb_id=&student_id=&lms_class=false&lms_launch=false&embed=true&playlistGradebook=${playlistId}`, '_blank');
    },
  },
  googleClassroom: {
    fetchGoogleStudentSubmissionId(data) {
      return axios.get(`${service.baseURL}google-classroom/student-submission?courseWorkId=${data.courseWorkId}&courseId=${data.courseId}&playlistAttemptId=${data.playlistAttemptId}`);
    },
    syncGrades(data) {
      return axios.post(`${service.baseURL}playlist/google-sync`,
        {
          courseId: data.courseId,
          courseWorkId: data.courseWorkId,
          playlistId: data.playlistId,
          sectionId: data.sectionId,
          studentSubmissionId: data.studentSubmissionId,
        });
    },
  },
  item: {
    delete(courseId, data) {
      return axios.delete(`${service.baseURL}playlist/${courseId}/module/${data.moduleId}/item/${data.itemId}?type=${data.type}`);
    },
    update(courseId, data) {
      return axios.put(`${service.baseURL}playlist/${courseId}/module/${data.moduleId}/item/${data.itemId}`, {
        updateData: data.itemConfig,
      });
    },
  },
  playlist: {
    addBadgrBadge({
      playlistId,
      badge,
      badgeService,
      issuerEntityId,
    } = {}) {
      return axios.post(`${service.baseURL}playlist/${playlistId}/badgr-badge`, {
        badge,
        badgeService,
        issuerEntityId,
      });
    },
    addBulbTeacherRow({
      bulbId,
      playlistId,
    } = {}) {
      return axios.post(`${service.baseURL}playlist/${playlistId}/bulb/${bulbId}/bulbTeacher`);
    },
    addCredlyBadge({
      playlistId,
      badge,
      badgeService,
    } = {}) {
      return axios.post(`${service.baseURL}playlist/${playlistId}/credly-badge`, {
        badge,
        badgeService,
      });
    },
    addCertificateItem(playlistId, message) {
      return axios.post(`${service.baseURL}playlist/certificate/${playlistId}/message`, message);
    },
    copy(playlistId) {
      return axios.get(`${service.baseURL}playlist/copy/${playlistId}`);
    },
    createAttachment(data) {
      return axios.post(`${service.baseURL}playlist/attachment`, data);
    },
    createCertificateItem(playlistId, file) {
      return axios.post(`${service.baseURL}playlist/certificate/${playlistId}/item`, file);
    },
    createModuleItemAttempt(data) {
      return axios.post(`${service.baseURL}playlist/module-item-attempt`, data);
    },
    async decodeUser(encodedId) {
      const url = getServerUrl();
      return axios.get(`${url}playlist/decode-user/${encodedId}`);
    },
    disableBadge({
      enableBadge,
      playlistId,
    } = {}) {
      return axios.put(`${service.baseURL}playlist/${playlistId}/badge`, {
        enableBadge,
      });
    },
    async encodeUser(userId) {
      const url = getServerUrl();
      return axios.get(`${url}playlist/encode-user/${userId}`);
    },
    createLmb({
      name,
      learningObjective,
      points,
      allowUpload,
      autograde,
      allowPrivateVideoHosts,
      learnersAccessTheirOwnVideoIntegrationContent,
    } = {}) {
      return axios.post(`${service.baseURL}instructor/learnermadebulbs`, {
        name,
        learningObjective,
        points,
        upload: allowUpload,
        grade: autograde,
        allowPrivateVideoHosts,
        learnersAccessTheirOwnVideoIntegrationContent,
      });
    },
    getOrCreateLMB({
      // playlistId,
      bulbId,
      sectionId,
    } = {}) {
      return axios.get(`${service.baseURL}playlist/lmb/${bulbId}/section/${sectionId}`);
    },
    getTags({
      isSearch,
      searchString,
    }) {
      return axios.get(`${service.baseURL}instructor/tags?isSearch=${isSearch}${searchString ? `&searchString=${searchString}` : ''}`);
    },
    async markComplete(data) {
      return axios.put(`${service.baseURL}playlist/${data.playlistId}/playlistAttempt`, {
        sectionId: data.sectionId,
      });
    },
    async PDRedirect() {
      return axios.get(`${service.baseURL}authnew/pd-redirect`);
    },
    removeBadge({
      badgeEntityId,
      badgeService,
      playlistId,
    } = {}) {
      return axios.delete(`${service.baseURL}playlist/${playlistId}/badge?badgeService=${badgeService}&badgeEntityId=${badgeEntityId}`);
    },
    resetModuleItemAttempt({
      moduleItemAttemptId,
      playlistId,
    } = {}) {
      return axios.put(`${service.baseURL}playlist/${playlistId}/module-item-attempt/${moduleItemAttemptId}/reset`);
    },
    saveTags({
      addTags,
      deleteTags,
      playlistId,
    } = {}) {
      return axios.post(`${service.baseURL}playlist/${playlistId}/tags`,
        {
          addTags,
          deleteTags,
        });
    },
    async syncLTI({
      consumerKey,
      resourceLinkId,
      sectionId,
      lti13Launch = false,
      issuer,
    } = {}) {
      let lti13LaunchParams = '';
      if (lti13Launch) lti13LaunchParams = `&lti13Launch=true&issuer=${issuer}`;
      return axios.get(`${getServerUrl()}ltidash/syncltigrades?consumer_key=${consumerKey}&resource_link_id=${resourceLinkId}&section_id=${sectionId}${lti13LaunchParams}`);
    },
    get(data) {
      return axios.get(`${service.baseURL}playlist/${data.playlistId}${data.sectionId ? `?sectionId=${data.sectionId}` : ''}`);
    },
    async getSimplified(data) {
      const url = getServerUrl();
      return axios.get(`${url}playlist/certificate/${data.playlistId}/${data.encodedId}${data.sectionId ? `?sectionId=${data.sectionId}` : ''}`);
    },
    paywall(courseId, playlist) {
      return axios.put(`${service.baseURL}playlist/${courseId}/paywall`, {
        updateData: playlist,
      });
    },
    removeCertificateItem(playlistId, key) {
      return axios.put(`${service.baseURL}playlist/certificate/${playlistId}/item?key=${key}`);
    },
    update(courseId, playlist) {
      return axios.put(`${service.baseURL}playlist/${courseId}`, {
        updateData: playlist,
      });
    },
    markModuleItemAttemptComplete(data) {
      return axios.patch(`${service.baseURL}playlist/${data.playlistId}/module-item-attempt`, data);
    },
    async validateWellbeingEmail(courseData) {
      const url = getServerUrl();
      return axios.get(`${url}loginsignup/wellbeing/${courseData.courseHash}/?email=${courseData.email}`);
    },
  },
  module: {
    addBulbs(courseId, moduleId, passbackBulbs) {
      return axios.post(`${service.baseURL}playlist/${courseId}/module/${moduleId}/bulbs`,
        {
          bulbs: passbackBulbs,
        });
    },
    addItem(courseId, data) {
      return axios.post(`${service.baseURL}playlist/${courseId}/module/${data.moduleId}/item`,
        {
          item: data.item,
          oldModuleId: data.oldModuleId,
          oldItemIndex: data.oldItemIndex,
        });
    },
    addLink(courseId, moduleId, link) {
      return axios.post(`${service.baseURL}playlist/${courseId}/module/${moduleId}/link`,
        {
          link,
        });
    },
    addText({
      playlistId,
      moduleId,
      text,
    } = {}) {
      return axios.post(`${service.baseURL}playlist/${playlistId}/module/${moduleId}/text`,
        {
          text,
        });
    },
    create(courseId, data) {
      return axios.post(`${service.baseURL}playlist/${courseId}/module`, {
        name: data.name,
        order: data.order,
        defaultColor: data.defaultColor,
      });
    },
    delete(courseId, moduleId) {
      return axios.delete(`${service.baseURL}playlist/${courseId}/module/${moduleId}`);
    },
    update(courseId, moduleId, module) {
      return axios.put(`${service.baseURL}playlist/${courseId}/module/${moduleId}`, {
        updateData: module,
      });
    },
    updateText({
      playlistId,
      moduleId,
      text,
    } = {}) {
      return axios.put(`${service.baseURL}playlist/${playlistId}/module/${moduleId}/text`,
        {
          text,
        });
    },
    getTags(tagSearchString) {
      return axios.get(`${service.baseURL}instructor/tags?searchString=${tagSearchString}&isSearch=true`);
    },
    getModuleBulbTags(bulbId) {
      return axios.get(`${service.baseURL}instructor/bulbs/${bulbId}/tags`);
    },
    updatePlaylistBulbSettings({
      playlistId,
      bulb,
    } = {}) {
      return axios.post(`${service.baseURL}playlist/updatebulbsettings/${playlistId}`, {
        bulb,
      });
    },
  },
};

export default service;
